import React, { useState, useRef } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  Flex,
  Progress,
} from "@chakra-ui/react";
import {
  FaArrowRight,
  FaCheckCircle,
  FaSolarPanel,
  FaBatteryFull,
  FaBolt,
  FaPhone,
  FaChartLine,
  FaHome,
  FaRedo,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

interface Appliance {
  id: string;
  label: string;
  emoji: string;
  highEnergy?: boolean;
}

const DAILY_APPLIANCES: Appliance[] = [
  { id: "kettle", label: "Kettle", emoji: "☕" },
  { id: "aircon", label: "Aircon", emoji: "❄️", highEnergy: true },
  { id: "heater", label: "Heater", emoji: "🔥", highEnergy: true },
  { id: "washing", label: "Washing Machine", emoji: "👕", highEnergy: true },
  { id: "dryer", label: "Dryer", emoji: "🌀", highEnergy: true },
  { id: "dishwasher", label: "Dishwasher", emoji: "🍽️", highEnergy: true },
  { id: "cooking", label: "Cooking", emoji: "🍳" },
  { id: "fridge", label: "Fridge", emoji: "🧊" },
  { id: "laptop", label: "Laptop", emoji: "💻" },
  { id: "tv", label: "TV / Streaming", emoji: "📺" },
  { id: "gaming", label: "Gaming", emoji: "🎮", highEnergy: true },
  { id: "hotwater", label: "Hot Water System", emoji: "🚿", highEnergy: true },
];

const WEEKLY_APPLIANCES: Appliance[] = [
  { id: "pool", label: "Pool Pump", emoji: "🏊", highEnergy: true },
  { id: "dryer_w", label: "Dryer", emoji: "🌀", highEnergy: true },
  { id: "extra_wash", label: "Extra Washing", emoji: "👚", highEnergy: true },
  { id: "outdoor_lights", label: "Outdoor Lights", emoji: "💡" },
  { id: "second_fridge", label: "Second Fridge", emoji: "🧊" },
  { id: "home_office", label: "Home Office", emoji: "🖥️" },
  { id: "ev", label: "EV Charging", emoji: "⚡", highEnergy: true },
  { id: "spa", label: "Spa / Hot Tub", emoji: "🛁", highEnergy: true },
];

const PEAK_OPTIONS = [
  { id: "daytime", label: "Mostly daytime", desc: "When we're home during the day", scoreBonus: 10 },
  { id: "mixed", label: "Even mix", desc: "Throughout the day and evenings", scoreBonus: 5 },
  { id: "night", label: "Mostly night", desc: "Evenings and after dinner", scoreBonus: 0 },
  { id: "owl", label: "Night owl mode", desc: "Late nights and early mornings", scoreBonus: -5 },
];

const PROFILE_OPTIONS = [
  { id: "switched_on", label: "We're pretty switched on", desc: "We actively monitor and manage our usage", scoreBonus: 10 },
  { id: "try_best", label: "We try our best", desc: "We're aware but could always do better", scoreBonus: 5 },
  { id: "convenience", label: "Convenience usually wins", desc: "We don't think about it much", scoreBonus: 0 },
  { id: "no_idea", label: "We have no idea where it goes", desc: "It's a complete mystery to us", scoreBonus: -5 },
];

const getPersonality = (daily: string[], weekly: string[], peak: string, profile: string) => {
  let score = 100;

  daily.forEach(id => {
    const found = DAILY_APPLIANCES.find(a => a.id === id);
    score -= found?.highEnergy ? 4 : 1;
  });

  weekly.forEach(id => {
    const found = WEEKLY_APPLIANCES.find(a => a.id === id);
    score -= found?.highEnergy ? 6 : 2;
  });

  const peakOpt = PEAK_OPTIONS.find(p => p.id === peak);
  const profileOpt = PROFILE_OPTIONS.find(p => p.id === profile);
  score += (peakOpt?.scoreBonus ?? 0) + (profileOpt?.scoreBonus ?? 0);
  score = Math.max(32, Math.min(97, score));

  const isNightHeavy = peak === "night" || peak === "owl";

  if (score >= 78 && peak === "daytime") {
    return {
      emoji: "☀️",
      badge: "The Solar Superstar",
      score,
      headline: "Your home is primed for solar.",
      analysis:
        "You run your biggest loads during the day — exactly when solar panels are producing peak power. You're in an ideal position to cover most of your energy needs directly from the sun and dramatically cut your bills.",
      system: "6.6kW – 10kW solar system",
      battery: "Battery optional — ideal for evening independence",
      accentColor: SOLAR_GOLD,
    };
  } else if (isNightHeavy) {
    return {
      emoji: "🌙",
      badge: "The Night Owl",
      score,
      headline: "You need solar + battery working together.",
      analysis:
        "Your home's heaviest loads run at night — meaning solar alone won't capture the full value. A solar and battery system lets you generate clean energy during the day, store it, and use it when your home demands it most.",
      system: "6.6kW – 13.3kW solar system",
      battery: "Battery storage essential (10kWh+)",
      accentColor: NAVY,
    };
  } else if (score >= 62) {
    return {
      emoji: "⚡",
      badge: "The Smart Switcher",
      score,
      headline: "You're energy-aware — now make it work for you.",
      analysis:
        "You've got a solid mix of habits and a realistic view of where your energy goes. A properly sized solar and battery package could cover 70–90% of your household needs and give you real independence from rising grid prices.",
      system: "6.6kW – 10kW solar system",
      battery: "Battery storage recommended for evening use",
      accentColor: "#2B7A0E",
    };
  } else {
    return {
      emoji: "🔌",
      badge: "The Heavy Hitter",
      score,
      headline: "Big energy appetite — and the biggest savings to match.",
      analysis:
        "Your home runs hard. The good news? Homes like yours typically see the greatest return from solar and battery — because there's so much usage to offset. A well-designed system can transform your bills.",
      system: "13.3kW+ solar system",
      battery: "Large battery storage essential (15kWh+)",
      accentColor: "#C53030",
    };
  }
};

const SolarHouseholdIQ: React.FC = () => {
  const [step, setStep] = useState(0);
  const [dailySelected, setDailySelected] = useState<Set<string>>(new Set());
  const [weeklySelected, setWeeklySelected] = useState<Set<string>>(new Set());
  const [peakTime, setPeakTime] = useState<string | null>(null);
  const [profileType, setProfileType] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const quizRef = useRef<HTMLDivElement>(null);

  const scrollToQuiz = () => {
    quizRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleDaily = (id: string) => {
    setDailySelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleWeekly = (id: string) => {
    setWeeklySelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectPeak = (id: string) => {
    setPeakTime(id);
    setTimeout(() => setStep(3), 300);
  };

  const handleSelectProfile = (id: string) => {
    setProfileType(id);
    setTimeout(() => setCompleted(true), 300);
  };

  const handleReset = () => {
    setStep(0);
    setDailySelected(new Set());
    setWeeklySelected(new Set());
    setPeakTime(null);
    setProfileType(null);
    setCompleted(false);
    scrollToQuiz();
  };

  const progress = completed ? 100 : (step / 4) * 100;
  const personality =
    completed && peakTime && profileType
      ? getPersonality(Array.from(dailySelected), Array.from(weeklySelected), peakTime, profileType)
      : null;

  const stepLabel = ["Step 1 of 4 — Daily Usage", "Step 2 of 4 — Weekly Usage", "Step 3 of 4 — Peak Time", "Step 4 of 4 — Your Profile"][step];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/household-iq"
        fallbackTitle="Household IQ™ — What's Eating Your Energy? | One Solar Australia"
        fallbackDescription="Take our free 60-second Household IQ™ check-up to discover how smart your home's energy use really is, and find the right solar solution for your household."
      />
      <NavbarWithCallToAction />

      {/* ── Hero ── */}
      <Box
        position="relative"
        color="white"
        py={{ base: 24, md: 32 }}
        backgroundImage="url('/assets/solar-assets/asset%2037.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.88)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 2px 8px rgba(198,146,20,0.5)"
            >
              Free 60-Second Energy Check-Up
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "5xl", md: "7xl" }}
              fontWeight="black"
              letterSpacing="tight"
              lineHeight="1"
            >
              HOUSEHOLD IQ™
            </Heading>
            <Heading size="xl" color={SOLAR_GOLD} fontWeight="700">
              What's Eating Your Energy?
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              A fun 60-second check-up to uncover how smart your home really is — and which solar solution fits it best.
            </Text>
            <Button
              size="lg"
              bg={SOLAR_GOLD}
              color="white"
              _hover={{ bg: SOLAR_GOLD_HOVER, transform: "translateY(-2px)", boxShadow: "xl" }}
              rightIcon={<FaArrowRight />}
              px={10}
              py={6}
              fontSize="lg"
              fontWeight="700"
              borderRadius="xl"
              boxShadow="0 4px 16px rgba(198,146,20,0.5)"
              transition="all 0.2s"
              onClick={scrollToQuiz}
            >
              Begin Investigation
            </Button>
            <HStack spacing={6} pt={2} flexWrap="wrap" justify="center">
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">Takes 60 seconds</Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">100% free</Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">No obligation</Text>
              </HStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ── Quiz Section ── */}
      <Box ref={quizRef as React.RefObject<HTMLDivElement>} bg="gray.50" py={{ base: 14, md: 20 }}>
        <Container maxW="container.md">
          <VStack spacing={8}>
            {!completed && (
              <VStack spacing={3} w="full">
                <Flex w="full" justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.500" fontWeight="600">{stepLabel}</Text>
                  <Text fontSize="sm" color={SOLAR_GOLD} fontWeight="700">~60 seconds</Text>
                </Flex>
                <Progress
                  value={progress}
                  w="full"
                  borderRadius="full"
                  size="sm"
                  colorScheme="yellow"
                  bg="gray.100"
                />
              </VStack>
            )}

            <Box
              w="full"
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 10 }}
              boxShadow="xl"
              border="2px solid"
              borderColor={`${SOLAR_GOLD}40`}
            >
              {/* Step 1 — Daily appliances (multi-select) */}
              {!completed && step === 0 && (
                <VStack spacing={6}>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="lg" color={NAVY}>Which of these do you use daily?</Heading>
                    <Text color="gray.500" fontSize="sm">Select everything that applies — the more honest, the smarter your result.</Text>
                  </VStack>
                  <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3} w="full">
                    {DAILY_APPLIANCES.map(app => {
                      const sel = dailySelected.has(app.id);
                      return (
                        <Box
                          key={app.id}
                          as="button"
                          onClick={() => toggleDaily(app.id)}
                          bg={sel ? SOLAR_GOLD : "white"}
                          color={sel ? "white" : NAVY}
                          border="2px solid"
                          borderColor={sel ? SOLAR_GOLD : "gray.200"}
                          borderRadius="xl"
                          p={3}
                          textAlign="center"
                          cursor="pointer"
                          transition="all 0.15s"
                          _hover={{ borderColor: SOLAR_GOLD, boxShadow: "md" }}
                          boxShadow={sel ? "0 2px 8px rgba(198,146,20,0.3)" : "none"}
                        >
                          <Text fontSize="2xl" mb={1}>{app.emoji}</Text>
                          <Text fontSize="xs" fontWeight="600" lineHeight="1.2">{app.label}</Text>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                  <Button
                    w="full"
                    size="lg"
                    bg={SOLAR_GOLD}
                    color="white"
                    _hover={{ bg: SOLAR_GOLD_HOVER }}
                    rightIcon={<FaArrowRight />}
                    onClick={() => setStep(1)}
                    isDisabled={dailySelected.size === 0}
                    transition="all 0.2s"
                  >
                    Next
                  </Button>
                </VStack>
              )}

              {/* Step 2 — Weekly appliances (multi-select) */}
              {!completed && step === 1 && (
                <VStack spacing={6}>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="lg" color={NAVY}>Which of these run weekly?</Heading>
                    <Text color="gray.500" fontSize="sm">These are the sneaky energy eaters that can push bills up.</Text>
                  </VStack>
                  <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3} w="full">
                    {WEEKLY_APPLIANCES.map(app => {
                      const sel = weeklySelected.has(app.id);
                      return (
                        <Box
                          key={app.id}
                          as="button"
                          onClick={() => toggleWeekly(app.id)}
                          bg={sel ? SOLAR_GOLD : "white"}
                          color={sel ? "white" : NAVY}
                          border="2px solid"
                          borderColor={sel ? SOLAR_GOLD : "gray.200"}
                          borderRadius="xl"
                          p={3}
                          textAlign="center"
                          cursor="pointer"
                          transition="all 0.15s"
                          _hover={{ borderColor: SOLAR_GOLD, boxShadow: "md" }}
                          boxShadow={sel ? "0 2px 8px rgba(198,146,20,0.3)" : "none"}
                        >
                          <Text fontSize="2xl" mb={1}>{app.emoji}</Text>
                          <Text fontSize="xs" fontWeight="600" lineHeight="1.2">{app.label}</Text>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                  <HStack spacing={3} w="full">
                    <Button
                      flex={1}
                      variant="outline"
                      borderColor="gray.200"
                      color={NAVY}
                      onClick={() => setStep(2)}
                    >
                      None apply
                    </Button>
                    <Button
                      flex={2}
                      size="lg"
                      bg={SOLAR_GOLD}
                      color="white"
                      _hover={{ bg: SOLAR_GOLD_HOVER }}
                      rightIcon={<FaArrowRight />}
                      onClick={() => setStep(2)}
                      transition="all 0.2s"
                    >
                      Next
                    </Button>
                  </HStack>
                </VStack>
              )}

              {/* Step 3 — Peak usage time (single-select, auto-advances) */}
              {!completed && step === 2 && (
                <VStack spacing={6}>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="lg" color={NAVY}>When does your home use the most power?</Heading>
                    <Text color="gray.500" fontSize="sm">This reveals whether your home is daytime-smart or night-time heavy.</Text>
                  </VStack>
                  <VStack spacing={3} w="full">
                    {PEAK_OPTIONS.map(opt => {
                      const sel = peakTime === opt.id;
                      return (
                        <Box
                          key={opt.id}
                          as="button"
                          onClick={() => handleSelectPeak(opt.id)}
                          w="full"
                          bg={sel ? `${SOLAR_GOLD}12` : "white"}
                          border="2px solid"
                          borderColor={sel ? SOLAR_GOLD : "gray.200"}
                          borderRadius="xl"
                          p={4}
                          textAlign="left"
                          cursor="pointer"
                          transition="all 0.15s"
                          _hover={{ borderColor: SOLAR_GOLD, bg: `${SOLAR_GOLD}08` }}
                        >
                          <HStack spacing={4}>
                            <Box
                              w={5} h={5}
                              borderRadius="full"
                              border="2px solid"
                              borderColor={sel ? SOLAR_GOLD : "gray.300"}
                              bg={sel ? SOLAR_GOLD : "white"}
                              flexShrink={0}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {sel && <Box w={2} h={2} bg="white" borderRadius="full" />}
                            </Box>
                            <VStack align="flex-start" spacing={0}>
                              <Text fontWeight="700" color={NAVY} fontSize="md">{opt.label}</Text>
                              <Text color="gray.500" fontSize="sm">{opt.desc}</Text>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </VStack>
              )}

              {/* Step 4 — Household profile (single-select, auto-advances) */}
              {!completed && step === 3 && (
                <VStack spacing={6}>
                  <VStack spacing={2} textAlign="center">
                    <Heading size="lg" color={NAVY}>What sounds most like your household?</Heading>
                    <Text color="gray.500" fontSize="sm">No judgement. This is where the smart analysis begins.</Text>
                  </VStack>
                  <VStack spacing={3} w="full">
                    {PROFILE_OPTIONS.map(opt => {
                      const sel = profileType === opt.id;
                      return (
                        <Box
                          key={opt.id}
                          as="button"
                          onClick={() => handleSelectProfile(opt.id)}
                          w="full"
                          bg={sel ? `${SOLAR_GOLD}12` : "white"}
                          border="2px solid"
                          borderColor={sel ? SOLAR_GOLD : "gray.200"}
                          borderRadius="xl"
                          p={4}
                          textAlign="left"
                          cursor="pointer"
                          transition="all 0.15s"
                          _hover={{ borderColor: SOLAR_GOLD, bg: `${SOLAR_GOLD}08` }}
                        >
                          <HStack spacing={4}>
                            <Box
                              w={5} h={5}
                              borderRadius="full"
                              border="2px solid"
                              borderColor={sel ? SOLAR_GOLD : "gray.300"}
                              bg={sel ? SOLAR_GOLD : "white"}
                              flexShrink={0}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {sel && <Box w={2} h={2} bg="white" borderRadius="full" />}
                            </Box>
                            <VStack align="flex-start" spacing={0}>
                              <Text fontWeight="700" color={NAVY} fontSize="md">{opt.label}</Text>
                              <Text color="gray.500" fontSize="sm">{opt.desc}</Text>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </VStack>
              )}

              {/* Results */}
              {completed && personality && (
                <VStack spacing={8}>
                  <VStack spacing={3} textAlign="center">
                    <Text fontSize="xs" fontWeight="800" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="widest">
                      Your Home Personality™
                    </Text>
                    <Box
                      fontSize="4xl"
                      w={20} h={20}
                      bg={`${personality.accentColor}15`}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="3px solid"
                      borderColor={`${personality.accentColor}40`}
                      mx="auto"
                    >
                      {personality.emoji}
                    </Box>
                    <Badge
                      bg={personality.accentColor}
                      color="white"
                      fontSize="md"
                      px={5} py={2}
                      borderRadius="full"
                      fontWeight="800"
                    >
                      {personality.badge}
                    </Badge>
                    <HStack justify="center" spacing={2}>
                      <Text fontSize="sm" color="gray.500" fontWeight="600">Household IQ™:</Text>
                      <Text fontSize="sm" fontWeight="900" color={personality.accentColor}>
                        {personality.score}/100
                      </Text>
                    </HStack>
                  </VStack>

                  <Box bg="gray.50" borderRadius="2xl" p={6} w="full">
                    <Text fontSize="xs" fontWeight="800" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="widest" mb={3}>
                      Smart Analysis
                    </Text>
                    <Heading size="md" color={NAVY} mb={3} lineHeight="1.3">{personality.headline}</Heading>
                    <Text color="gray.600" fontSize="sm" lineHeight="1.8">{personality.analysis}</Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <Box
                      bg="white"
                      border="2px solid"
                      borderColor={`${SOLAR_GOLD}30`}
                      borderRadius="xl"
                      p={5}
                    >
                      <HStack spacing={3} mb={2}>
                        <Icon as={FaSolarPanel} color={SOLAR_GOLD} boxSize={5} />
                        <Text fontSize="xs" fontWeight="800" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="wider">
                          Recommended System
                        </Text>
                      </HStack>
                      <Text fontWeight="700" color={NAVY} fontSize="md">{personality.system}</Text>
                    </Box>
                    <Box
                      bg="white"
                      border="2px solid"
                      borderColor={`${SOLAR_GOLD}30`}
                      borderRadius="xl"
                      p={5}
                    >
                      <HStack spacing={3} mb={2}>
                        <Icon as={FaBatteryFull} color={SOLAR_GOLD} boxSize={5} />
                        <Text fontSize="xs" fontWeight="800" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="wider">
                          Battery
                        </Text>
                      </HStack>
                      <Text fontWeight="700" color={NAVY} fontSize="md">{personality.battery}</Text>
                    </Box>
                  </SimpleGrid>

                  <Box
                    w="full"
                    bg={NAVY}
                    borderRadius="2xl"
                    p={6}
                    textAlign="center"
                  >
                    <Heading size="md" color="white" mb={2}>Make your home even smarter</Heading>
                    <Text color="gray.300" fontSize="sm" mb={5}>
                      Submit your details to see your solar and battery options based on your Household IQ™ result.
                    </Text>
                    <VStack spacing={3}>
                      <Button
                        as={RouterLink}
                        to="/solar/quote"
                        size="lg"
                        w="full"
                        bg={SOLAR_GOLD}
                        color="white"
                        _hover={{ bg: SOLAR_GOLD_HOVER }}
                        rightIcon={<FaArrowRight />}
                        fontWeight="700"
                        transition="all 0.2s"
                      >
                        Show My Solar & Battery Options
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        leftIcon={<FaRedo />}
                        onClick={handleReset}
                        _hover={{ color: "white" }}
                      >
                        Retake the quiz
                      </Button>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ── How It Works ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
            How It Works
          </Badge>
          <Heading size="xl" color={NAVY}>Four Questions. One Clear Answer.</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Household IQ™ analyses your energy habits to match you with the right solar solution — no guesswork.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={8}>
          {[
            { step: "01", icon: FaBolt, title: "Daily Loads", body: "We map your everyday appliances — the big ones and the background drains." },
            { step: "02", icon: FaChartLine, title: "Weekly Habits", body: "We layer in your weekly usage to build a full picture of your energy profile." },
            { step: "03", icon: FaHome, title: "Peak Time", body: "When your home is hardest-working shapes the entire solar and battery recommendation." },
            { step: "04", icon: FaCheckCircle, title: "Your IQ Score", body: "Instant personalised result — your Home Personality™ and the exact system to match." },
          ].map(item => (
            <Box
              key={item.step}
              bg="white"
              borderRadius="2xl"
              p={8}
              boxShadow="md"
              border="1px solid"
              borderColor="gray.100"
              _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
              transition="all 0.3s"
              textAlign="center"
            >
              <Box
                w={12} h={12}
                bg={SOLAR_GOLD}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="black"
                fontSize="lg"
                color="white"
                mb={5}
                mx="auto"
                boxShadow="0 2px 8px rgba(198,146,20,0.4)"
              >
                {item.step}
              </Box>
              <Icon as={item.icon} boxSize={8} color={SOLAR_GOLD} mb={4} />
              <Heading size="md" color={NAVY} mb={3}>{item.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="1.8">{item.body}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Final CTA ── */}
      <Box
        position="relative"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/solar-assets/asset%2013.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.90)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" color="white">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 2px 8px rgba(198,146,20,0.5)"
            >
              Ready to Act?
            </Badge>
            <Heading size="2xl" fontWeight="black" maxW="3xl" lineHeight="1.2">
              Your Household IQ™ Result is Just the Start
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              Book a free, no-obligation assessment and let our CEC-accredited team design the exact system your home needs.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={10}
                transition="all 0.2s"
              >
                Get My Free Assessment
              </Button>
              <Button
                as="a"
                href="tel:1300663765"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                leftIcon={<FaPhone />}
                px={8}
                transition="all 0.2s"
              >
                1300 663 765
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" pt={2}>
              Free assessment. No obligation. CEC accredited installers. ACN 642 690 435
            </Text>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarHouseholdIQ;
